import { Controller } from "@hotwired/stimulus"

// Cards <-> table toggle for the index surfaces (Tasks, Processes).
//
// On phones the card list is the default and the Tabulator table is hidden
// (CSS: cards are `xl:hidden`, the table is `max-xl:hidden`). This controller
// adds an optional "Table"/"Cards" toggle, shown only below the xl breakpoint,
// that flips which one is visible and remembers the choice in localStorage.
// At xl+ the CSS already forces the table, so the toggle is inert there.
export default class extends Controller {
  static targets = ["cards", "table", "label"]
  static values = { key: { type: String, default: "index-view" } }

  connect() {
    this.current = "cards"
    // Only honour a stored preference below desktop; xl+ is always the table.
    if (window.matchMedia("(min-width: 1280px)").matches) return
    // The cards frame is `loading="lazy"`, but Turbo's IntersectionObserver
    // doesn't reliably fire for a frame already in the viewport at page load,
    // so it can sit on "Loading…" forever. Nudge it to load now. Desktop
    // returns above, so the unbounded tasks query still never runs there.
    this.loadCards()
    if (localStorage.getItem(this.keyValue) === "table") this.show("table")
  }

  // Force a deferred cards turbo-frame to fetch its source (mobile only).
  loadCards() {
    if (!this.hasCardsTarget) return
    const f = this.cardsTarget
    if (f.tagName !== "TURBO-FRAME") return
    if (!f.getAttribute("src") || f.hasAttribute("complete")) return
    f.setAttribute("loading", "eager")
    if (typeof f.reload === "function") {
      try { f.reload() } catch (_) {}
    }
  }

  toggle() {
    const next = this.current === "table" ? "cards" : "table"
    this.show(next)
    localStorage.setItem(this.keyValue, next)
  }

  show(mode) {
    this.current = mode
    const showTable = mode === "table"
    if (this.hasCardsTarget) this.cardsTarget.classList.toggle("max-xl:hidden", showTable)
    if (this.hasTableTarget) {
      this.tableTarget.classList.toggle("max-xl:hidden", !showTable)
      // Tabulator mis-sizes while display:none — tell its controller to redraw.
      if (showTable) {
        this.tableTarget.dispatchEvent(new CustomEvent("index-view:reveal", { bubbles: true }))
      }
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = showTable ? "Cards" : "Table"
  }
}
